import * as React from 'react';
import { useLocation } from "react-router-dom";
import { Box, Group, Button, createStyles, Anchor, Menu } from '@mantine/core'
import axios from 'axios';
import { CLIENT_ROUTE, API_ROUTE } from "./constants";
import { showMessage } from "../../../lib/notification";
import type { IResponse } from "../../../types/api";
import Page from "../../../components/layout/Page";
import { map } from 'ramda';
import { IModifyRequest, IModifyRequestStatus } from "./types";
import dayjs from "dayjs";
import { useSongStats } from "../../../hooks";

interface StatusOption {
    name: IModifyRequestStatus;
    label: string;
    color: string;
}

const statuses: StatusOption[] = [
    { name: 'resolved', label: 'Шийдвэрлэсэн', color: 'green' },
    { name: 'created', label: 'Үүссэн', color: 'yellow' },
    { name: 'rejected', label: 'Буцаагдсан', color: 'red' },
]

interface DictionaryItem<T> {
    key: keyof T;
    label: string;
}

const requestDictionary: DictionaryItem<IModifyRequest>[] = [
    { key: 'status', label: 'Төлөв' },
    { key: 'artistName', label: 'Уран бүтээлч' },
    { key: 'songCode1', label: 'Код-1' },
    { key: 'songTitle', label: 'Дууны нэр' },
    { key: 'createdAt', label: 'Хүсэлт илгээсэн огноо' },
    { key: 'updatedAt', label: 'Хүсэлт засварласан огноо' },
    { key: 'description', label: 'Тайлбар' },
    { key: 'attachment', label: 'Хавсаргасан файл' },
    { key: 'request', label: 'Засварлах мэдээллүүд' },
]

const modifyDictionary: DictionaryItem<IModifyRequest['request']>[] = [
    { key: 'title', label: 'Дууны нэр' },
    { key: 'titleEn', label: 'Дууны нэр EN' },
    { key: 'description', label: 'Тайлбар' },
    { key: 'vocal', label: 'Вокал' },
    { key: 'image', label: 'Профайл зураг' },
    { key: 'cover', label: 'Ковер зураг' },
    { key: 'categoryNumber', label: 'Сэдэв' },
    { key: 'lyricBy', label: 'Үг бичсэн' },
    { key: 'musicBy', label: 'Ая зохиосон' },
    { key: 'singer', label: 'Дуучин' },
    { key: 'singerEn', label: 'Дуучин EN' },
    { key: 'producedBy', label: 'Producer' },
    { key: 'label', label: 'Label' },
    { key: 'lyrics', label: 'Дууны үг' },
    { key: 'youtubeLink', label: 'YouTube холбоос' },
];

interface ISongItem {
    label: string;
    value: React.ReactNode;
}

const useStyles = createStyles((theme) => {
    return {
        modify: {
            borderRadius: theme.radius.md,
            background: theme.colors.gray[3],
            padding: theme.spacing.md,
            marginTop: theme.spacing.lg
        },
        item: {
            display: 'flex',
            width: '100%',
            gap: theme.spacing.md,
            ' > div': {
                flex: 1,
                wordBreak: 'break-word',
                '&:first-of-type': {
                    textAlign: 'right'
                },
                '&:last-of-type': {
                    fontWeight: 500
                }
            }
        },
        title: {
            fontWeight: 500,
            fontSize: 17
        }
    }
})

const SongItem = ({
    value,
    label
}: ISongItem) => {
    const { classes } = useStyles();
    return (
        <div className={classes.item}>
            <div>{label}</div>
            <div>{value}</div>
        </div>
    )
}

const EditSongModifyRequest = () => {

    const [ requestData, setRequestData ] = React.useState<IModifyRequest | null>(null);
    const fetchSongStats = useSongStats();

    const { state: id = null } = useLocation();
    const { classes } = useStyles();

    React.useEffect(() => {

        void fetchData();
    }, []);

    const fetchData = async () => {
        const { data } = await axios.get<IResponse<any>>(`${API_ROUTE}/${id}`);
        setRequestData(data.result)
    }

    const renderSongModifyInfo = () => {
        return (
            <div className={classes.modify}>
                <div className={classes.title}>
                    Засагдах утгууд:
                </div>
                {
                    map(renderModifyInfo, modifyDictionary ?? [])
                }
            </div>
        )
    }

    const getSongInfoValue = (key: keyof IModifyRequest): React.ReactNode => {
        if (key === 'request') {
            return renderSongModifyInfo();
        }
        const value = requestData?.[key];
        switch (key) {
            case 'updatedAt':
            case 'createdAt': {
                const value = requestData?.[key];
                if (!value) {
                    return '-';
                }
                return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
            }
            case 'attachment': {
                if (typeof value === 'string') {
                    return (
                        <Anchor target='_blank' href={value}>
                            Нээх
                        </Anchor>
                    )
                }
                return '-';
            }
            default: {
                return typeof value === 'string' ? value : '-';
            }
        }
    }

    const getModifyInfoValue = (key: keyof IModifyRequest['request']): React.ReactNode => {
        const value = requestData?.request?.[key];
        switch (key) {
            case 'updatedAt':
            case 'createdAt': {
                const value = requestData?.[key];
                if (!value) {
                    return '-';
                }
                return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
            }

            default: {
                return typeof value === 'string' ? value : '-';
            }
        }
    }

    const renderSongInfo = (item: DictionaryItem<IModifyRequest>) => {
        if (item.key === 'request') {
            return renderSongModifyInfo();
        }
        if (!requestData?.[item.key]) {
            return null;
        }
        const value = getSongInfoValue(item.key);
        return (
            <SongItem
                key={item.key}
                label={item.label}
                value={value}
            />
        )
    }

    const renderModifyInfo = (item: DictionaryItem<IModifyRequest['request']>) => {
        if (!requestData?.request?.[item.key]) {
            return null;
        }
        const value = getModifyInfoValue(item.key);
        return (
            <SongItem
                key={item.key}
                label={item.label}
                value={value}
            />
        )
    }

    const handleStatusChange = async (status: StatusOption) => {
        const params = {
            status: status.name
        }
        const { data } = await axios.post<IResponse<any>>(`/api/song-modify-request/${id}`, params);
        if (data.statusCode === 200) {
            await fetchData();
            showMessage('Амжилттай', 'green');
            void fetchSongStats();
        }
    }

    return (
        <Page>
            <Page.Header title={'Дуу засварлах хүсэлт'} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 1000 }} mx="auto">
                    {
                        map(renderSongInfo, requestDictionary ?? [])
                    }
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Menu shadow="md" width={200} position='top-end' >
                        <Menu.Target>
                            <Button>Төлөв солих</Button>
                        </Menu.Target>

                        <Menu.Dropdown>
                            {
                                statuses.map((status) => {
                                    return (
                                        <Menu.Item
                                            key={status.name}
                                            color={status.color}
                                            onClick={() => handleStatusChange(status)}
                                        >
                                            {status.label}
                                        </Menu.Item>
                                    )
                                })
                            }
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditSongModifyRequest;